import React, { useEffect, useState, Suspense } from 'react';
import { Typography, Tooltip } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { uploadFieldMaterials, importMaterials, convertImportResultToMaterial } from 'utils/FieldMaterialsUtils';
import { useAppContext } from 'hooks/useAppContext';
import AssignFieldForms from 'components/Materials/AssignFieldForms';
import AuthCheck from 'components/Shared/AuthCheck';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { deepCopy } from 'utils/SchemaUtils';
import FormProvider from './FormProvider';
import ImportToolbar from './ImportToolbar';
import FieldMaterials from 'classes/FieldMaterials';

const Grid = React.lazy(() => import('./AddMaterialGrid'));

/**
 * This component has a memory leak somewhere that I can't figure out. I added a lot of lazy re-load logic to slow down the renders in hope that the 
 * browser will catch up, but it can still be troublesome. when you see awaits on setTimeout promises, thats the lags that I introduced to slow down
 * re-renders of the data grid. -NR
 * @param {*} params.callback a function to call when save is successfully performed;
 */
const ImportMaterials = ({ callback }) => {
    const [open, setOpen] = useState(false);
    const [importedData, setImportedData] = useState([]);
    const [message, setMessage] = useState("");
    const [selectedMaterial, setSelectedMaterials] = useState([]);
    const [columns, setColumns] = useState(deepCopy(FieldMaterials.columns));
    const { selectedProject } = useAppContext();
    const [gridLoading, setGridLoading] = useState(true);
    const [checkedRecords, setCheckedRecords] = useState([]);
    const title = 'Import Materials';


    useEffect(() => {
        setGridLoading(true);
        setColumns(_columns => {
            const newColumns = deepCopy(_columns);
            newColumns.find(r => r.field === "Error").render = (r) => r.Error ? <Tooltip title={r.Error} placement="top"><ErrorOutlineIcon color="error" /></Tooltip> : null;
            if (importedData.find(r => !!r.Error)) { newColumns.find(r => r.field === "Error").hidden = false; };
            return newColumns;
        });
        setGridLoading(false);
    }, [importedData, message]);

    const handleFormProviderClose = () => {
        setOpen(false)
    };

    const handleErrorRecords = async (errorData) => {
        try {
            const parsedErrorData = JSON.parse(errorData);
            setGridLoading(true);
            await new Promise(res => setTimeout(res, 1000));
            setImportedData(oldImportedData => {
                const newData = [];
                oldImportedData.forEach((value, index) => {
                    const newVal = value;
                    const isExisting = parsedErrorData.Existing.find(r => newVal.FieldMaterialId === r.FieldMaterialId);
                    if (isExisting) {
                        newVal.Error = 'Record already exists.';
                    };
                    const isError = parsedErrorData['Item Errors'].find(r => newVal.FieldMaterialId === r.FieldMaterialId);
                    if (isError) {
                        newVal.Error = 'Attribute error.';
                    };

                    if(!isExisting && !isError) {
                        newVal.Error = undefined;
                    };

                    newData.push(newVal);
                });
                return newData;
            });
        } catch (e) {
            setGridLoading(false);
            return e;
        };
        setGridLoading(false);
    }

    const getFormPath = () => import('components/Materials/AddMaterials')

    const updateWorkItems = (data) => {
        setOpen(true)
        setSelectedMaterials(data)
    }

    const deleteImportedRecord = async (record) => {
        setGridLoading(true);
        setImportedData(importedData.filter(r => r.FieldMaterialId !== record.FieldMaterialId));
        await new Promise(res => setTimeout(res, 1000));
        setGridLoading(false);
    }

    const updateImportedRecord = (updatedRecord) => {
        setGridLoading(true);
        setImportedData(_oldData => {
            const newData = _oldData.map(old => {
                if (old.FieldMaterialId === updatedRecord.FieldMaterialId) {
                    return updatedRecord;
                } else {
                    return old
                };
            });
            return newData;
        });
        setGridLoading(false);
    }

    const updateFieldForms = (values, formList) => {
        const formName = values.split(',');
        selectedMaterial.forEach(sItem => {
            sItem.FieldForms = values
            sItem.FieldFormId = formList.filter((form) => formName.includes(form.TestType)).map(value => value.FormId)
        })
        const map = new Map(selectedMaterial.map(o => [o.FieldMaterialId, o]))
        const result = importedData.map(o => Object.assign({}, o, map.get(o.FieldMaterialId)));
        setImportedData(result)
        handleFormProviderClose()
    };

    const selection = { "display": true, "callback": updateWorkItems, "tooltip": "Assign Field Forms", "btnText": "Assign Field Forms" }

    const getRecords = React.useCallback(async () => {
        await new Promise(res => setTimeout(res, 1000));
        return new Promise(res => res({ data: importedData, page: 0, totalCount: importedData.length }))
    }, [importedData]);

    return (
        <AuthCheck action="create" scope="Materials" showWarning={true}>
            <div style={{marginTop: 12}}>
                <ImportToolbar
                    importedDataStore={importedData}
                    updateImportedDataStore={setImportedData}
                    updateStatusFunction={setMessage}
                    uploadFileAjaxFunction={uploadFieldMaterials}
                    saveDataAjaxFunction={importMaterials}
                    convertDataFunction={convertImportResultToMaterial}
                    handleUploadError={handleErrorRecords}
                    successfulSaveCallback={callback}
                    selectedRecords={checkedRecords}
                />
                <div style={{ textAlign: 'left', paddingLeft: '20px', paddingTop: '20px' }}>
                    <Alert severity="info" style={{ maxWidth: 600, margin: 4, marginBottom: '15px', backgroundColor: '#ffebee', color: '#c62828' }}>
                        <AlertTitle style={{ color: '#c62828', fontWeight: 'bold' }}>Material Import Template</AlertTitle>
                        Need a material import template? Please contact support for assistance.
                    </Alert>
                    {message.length > 0 && <Alert severity="error" style={{ maxWidth: 500, margin: 4 }}><AlertTitle>Error</AlertTitle>{message}</Alert>}
                </div>
                <FormProvider.Provider value={open}>
                    <AssignFieldForms onClick={handleFormProviderClose} updateForms={updateFieldForms} />
                </FormProvider.Provider>
                <div style={{ paddingTop: '20px' }}>
                    {importedData.length > 0 && !gridLoading ?
                        <Suspense fallback={<div>Loading...</div>}>
                            <Grid columns={columns} title={title} key={gridLoading} updateImportedRecord={updateImportedRecord} selectedProject={selectedProject} component="Materials" formPath={getFormPath} paging={false} data={getRecords} selection={selection} deleteImportedRecord={deleteImportedRecord} onSelectionChange={(rows) => setCheckedRecords(rows)} />
                        </Suspense> 
                        // <MaterialTable columns={columns} title={title} data={getRecords} />
                        :
                        <Typography color="textSecondary" gutterBottom style={{ paddingLeft: '25px', paddingBottom: '15px' }}>
                            {gridLoading ? 'loading...' : 'No records imported !'}
                        </Typography>
                    }
                </div>
            </div>
        </AuthCheck>
    )
}

export default ImportMaterials